import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { recorrenciaApi } from '@/api/endpoints/recorrencia';
import type { CancelarInput, ModoCobranca } from '@/types/recorrencia.types';

const chave = (userId: number) => ['recorrencia', userId];

export function useRecorrencia(userId: number | null, habilitado = true) {
  return useQuery({
    queryKey: chave(userId ?? 0),
    queryFn: () => recorrenciaApi.obter(userId as number),
    enabled: Boolean(userId) && habilitado,
    staleTime: 30 * 1000,
  });
}

function useInvalidar(userId: number) {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: chave(userId) });
    queryClient.invalidateQueries({ queryKey: ['user-subscription', userId] });
    queryClient.invalidateQueries({ queryKey: ['cobrancas'] });
  };
}

export function useGerarFatura(userId: number) {
  const invalidar = useInvalidar(userId);
  return useMutation({
    mutationFn: () => recorrenciaApi.gerarFatura(userId),
    onSuccess: invalidar,
  });
}

export function useReenviarFatura(userId: number) {
  const invalidar = useInvalidar(userId);
  return useMutation({
    mutationFn: (faturaId: number) => recorrenciaApi.reenviar(faturaId),
    onSuccess: invalidar,
  });
}

export function useCancelarAssinatura(userId: number) {
  const invalidar = useInvalidar(userId);
  return useMutation({
    mutationFn: (dados: CancelarInput) => recorrenciaApi.cancelar(userId, dados),
    onSuccess: invalidar,
  });
}

export function useAtualizarRecorrencia(userId: number) {
  const invalidar = useInvalidar(userId);
  return useMutation({
    mutationFn: (dados: { modo_cobranca?: ModoCobranca; whatsapp_cobranca?: boolean }) =>
      recorrenciaApi.atualizar(userId, dados),
    onSuccess: invalidar,
  });
}
